import Cookies from "js-cookie";
import api from "../pages/chat/api";
import { waitForUploadStatus } from "./uploadStatus";

/* Draft ONE node of the proposal and watch it happen.
 *
 * The handoff specified two new endpoints for this (proposal/section/generate/
 * and .../regenerate/). Neither was built, and neither needs to be: the project
 * screen has been drafting one node per request for a long time, over exactly
 * the frames this needs. So this is a thin wrapper over what exists rather than
 * a second way to write a section — the alternative is two code paths that
 * drift, and the way that shows up is a section drafted from Sensa reading
 * differently from the same section drafted from the project page.
 *
 * Passing a SECTION id drafts the whole family — its intro and every still-empty
 * subsection — in one LLM call (see _draft_section_family). The docstrings on
 * the view and the task still claim it writes the intro alone; they are stale,
 * and believing them cost this client six round trips per section and worse
 * prose, because each child was written in a prompt that could not see its
 * siblings.
 *
 * Returns { promise, cancel }. The promise resolves { ok } — false only on an
 * explicit error frame, because a dropped socket does not stop the worker.
 */

// Matches the safety net the project screen uses. A section that has genuinely
// hung is better reported as finished-and-refetch than left spinning forever.
const DRAFT_TIMEOUT_MS = 5 * 60 * 1000;

/* A side channel for the text, so the document can watch it being written.
 *
 * NOT React state lifted to the page. Deltas arrive several times a second, and
 * putting them through the page would re-render the entire transcript — every
 * message, every card — for text that only one panel displays. A subscription
 * lets the panel re-render alone, which is the difference between a document
 * that types and a conversation that stutters.
 *
 * Events: { type: "start" | "delta" | "done", targetId, delta? }
 */
const listeners = new Set();

export const onDraftEvent = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const emit = (event) => {
  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch (e) {
      /* one bad subscriber must not stop the others */
    }
  });
};

export const draftNode = ({
  folderId,
  targetId,
  isSubsection,
  instruction = "",
  onProgress,
  onStream,
}) => {
  let cancelWait = null;
  let cancelled = false;

  const promise = (async () => {
    const orgId = Cookies.get("selected_org_id");
    const qs = orgId ? `?organization_id=${encodeURIComponent(orgId)}` : "";

    /* An instruction means "change this", which is the regenerate endpoint —
     * it re-drafts against the node's stored TOC query plus the note. Without
     * one it is a first draft. Both answer 202 with a status_key and report on
     * the same socket, so only the URL and body differ. */
    const url = instruction
      ? `api/v1/rfp/folders/${folderId}/regenerate/${qs}`
      : `api/v1/rfp/folders/${folderId}/generate-subsections/${qs}`;

    const body = instruction
      ? { target_id: targetId, custom_prompt: instruction }
      : isSubsection
      ? { subsection_id: targetId }
      : { section_id: targetId };

    let statusKey;
    try {
      const res = await api.post(url, body);
      statusKey = res?.data?.status_key;
    } catch (e) {
      return { ok: false, error: e?.response?.data?.error || "Couldn't start drafting." };
    }
    if (cancelled) return { ok: false, error: "cancelled" };

    emit({ type: "start", targetId });

    let failed = null;
    const wait = waitForUploadStatus({
      statusKey,
      timeoutMs: DRAFT_TIMEOUT_MS,
      onProgress,
      onStream: (delta, frame) => {
        emit({ type: "delta", targetId, delta });
        onStream?.(delta, frame);
      },
      onError: (d) => {
        failed = d?.message || "Drafting failed.";
      },
    });
    cancelWait = wait.cancel;

    const ok = await wait.promise;
    // Announced on every exit, including failure — a watcher that only hears
    // about success leaves a node stuck mid-write on screen forever.
    emit({ type: "done", targetId });
    return ok && !failed ? { ok: true } : { ok: false, error: failed || "Drafting failed." };
  })();

  return {
    promise,
    cancel: () => {
      cancelled = true;
      cancelWait?.();
    },
  };
};

// Internal: the only caller is sectionIsWritten below. The writing card used
// to import it, back when it drafted node by node; it now sends one request
// per section and has no use for the list.
const nodesForSection = (section) => [
  // `words` rides along so the caller can tell an unwritten node from one that
  // is already saved. Without it every resume redrafts work that exists.
  {
    id: section.id,
    title: section.title,
    number: section.number,
    words: section.words || 0,
    isSubsection: false,
  },
  ...(section.children || []).map((c) => ({
    id: c.id,
    title: c.title,
    number: c.number,
    words: c.words || 0,
    isSubsection: true,
  })),
];

/* Is every node of this section already drafted?
 *
 * Used to decide where a reloaded conversation picks up. Lives here beside
 * nodesForSection so the two agree on what "a section's nodes" means - the page
 * and the writing card both ask this question and must not answer differently.
 */
export const sectionIsWritten = (section) =>
  nodesForSection(section).every((n) => n.words > 0);

export default draftNode;
